// Client side of the Bazzef SFU (sfu/ in the repo root). Each participant
// keeps one peer connection to the media server instead of one to the other
// caller, so calls work across any two networks as long as each side can
// reach the server (it accepts ICE over UDP and, as a fallback, TCP).
//
// The server is always the offerer: we only answer. It sends a new offer when
// someone's tracks come or go, or when we ask for an ICE restart after a
// network change. If the signaling socket drops, we reconnect and resume the
// same peer connection; the server keeps our seat for ~30s.

const RECONNECT_DELAYS_MS = [250, 500, 1000, 2000, 4000, 4000, 4000, 4000, 4000];
const RESTART_AFTER_DISCONNECT_MS = 2500;
const FAIL_AFTER_MS = 25000;

export class SfuSession {
  /**
   * @param {object} opts
   * @param {string} opts.url            SFU WebSocket URL (from the call grant)
   * @param {string} opts.token          join token (from the call grant)
   * @param {Array}  opts.iceServers
   * @param {(config: RTCConfiguration) => RTCPeerConnection} opts.createPeer
   *        builds a peer connection with the local tracks already added
   * @param {(e: RTCTrackEvent) => void} [opts.onTrack]   e.streams[0].id is the sender's user id
   * @param {() => void} [opts.onConnected]
   * @param {(reason: string) => void} [opts.onFailed]    media could not be (re)established
   * @param {(reason: string) => void} [opts.onClosed]    the server ended our session
   * @param {(msg: object) => void} [opts.onEvent]        peer-joined / peer-left
   */
  constructor(opts) {
    Object.assign(this, opts);
    this.ws = null;
    this.pc = null;
    this.closed = false;
    this.attempt = 0;
    this.pendingCandidates = [];
    this.queue = Promise.resolve(); // handle messages strictly in order
    this.timers = {};
    this.onOnline = () => this.requestRestart();
  }

  start() {
    window.addEventListener('online', this.onOnline);
    this.connect(false);
  }

  // Turn the camera on mid-call. The server's offers always carry a video
  // slot, so addTrack reuses it and one renegotiation starts sending.
  // Returns the RTCRtpSender (for replaceTrack when flipping cameras).
  publishTrack(track, stream) {
    const sender = this.pc?.addTrack(track, stream);
    this.renegotiate();
    return sender;
  }

  // Ask for a fresh offer after the local tracks changed outside publishTrack
  // (iOS adds its camera natively).
  renegotiate() {
    this.send({ type: 'renegotiate' });
  }

  close() {
    if (this.closed) return;
    this.closed = true;
    window.removeEventListener('online', this.onOnline);
    Object.values(this.timers).forEach(clearTimeout);
    this.send({ type: 'leave' });
    const { ws, pc } = this;
    this.ws = null;
    this.pc = null;
    ws?.close();
    pc?.close();
  }

  // ── Signaling ───────────────────────────────────────────────────────

  connect(resume) {
    const ws = new WebSocket(this.url);
    this.ws = ws;
    ws.onopen = () => {
      this.attempt = 0;
      this.send({ type: 'join', token: this.token, resume });
    };
    ws.onmessage = (e) => {
      let msg;
      try { msg = JSON.parse(e.data); } catch { return; }
      this.queue = this.queue
        .then(() => (this.closed || this.ws !== ws ? null : this.handle(msg)))
        .catch((err) => console.error('SFU signaling error:', err));
    };
    ws.onclose = () => {
      if (this.closed || this.ws !== ws) return;
      this.ws = null;
      this.scheduleReconnect();
    };
  }

  scheduleReconnect() {
    if (this.attempt >= RECONNECT_DELAYS_MS.length) {
      this.fail('signaling_lost');
      return;
    }
    const delay = RECONNECT_DELAYS_MS[this.attempt++];
    this.timers.reconnect = setTimeout(() => {
      if (!this.closed) this.connect(Boolean(this.pc));
    }, delay);
  }

  send(msg) {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(msg));
  }

  async handle(msg) {
    switch (msg.type) {
      case 'joined':
        // The server no longer had our seat: start over with a fresh connection
        if (!msg.resumed && this.pc) this.resetPeer();
        break;
      case 'offer':
        await this.answer(msg.sdp);
        break;
      case 'candidate':
        if (this.pc?.remoteDescription) {
          await this.pc.addIceCandidate(msg.candidate).catch((err) => console.warn('SFU ICE candidate error', err));
        } else {
          this.pendingCandidates.push(msg.candidate);
        }
        break;
      case 'error':
        this.fail(msg.error || 'error');
        break;
      case 'closed':
        this.end(msg.error || 'closed');
        break;
      default:
        this.onEvent?.(msg);
    }
  }

  async answer(offer) {
    if (!this.pc) this.pc = this.newPeer();
    const { pc } = this;
    await pc.setRemoteDescription(offer);
    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);
    const local = pc.localDescription || answer;
    this.send({ type: 'answer', sdp: { type: local.type, sdp: local.sdp } });

    const queued = this.pendingCandidates;
    this.pendingCandidates = [];
    for (const c of queued) {
      await pc.addIceCandidate(c).catch((err) => console.warn('SFU ICE candidate error', err));
    }
  }

  // ── Media ───────────────────────────────────────────────────────────

  newPeer() {
    const pc = this.createPeer({ iceServers: this.iceServers || [] });
    pc.onicecandidate = (e) => {
      if (!e.candidate) return;
      const c = typeof e.candidate.toJSON === 'function' ? e.candidate.toJSON() : e.candidate;
      this.send({ type: 'candidate', candidate: c });
    };
    pc.ontrack = (e) => this.onTrack?.(e);
    pc.onconnectionstatechange = () => this.onMediaState(pc, pc.connectionState);
    // Older WebKit only reports ICE state
    pc.oniceconnectionstatechange = () => {
      const s = pc.iceConnectionState;
      this.onMediaState(pc, s === 'completed' ? 'connected' : s);
    };
    return pc;
  }

  resetPeer() {
    this.pc?.close();
    this.pc = null;
    this.pendingCandidates = [];
  }

  onMediaState(pc, state) {
    if (pc !== this.pc || this.closed) return;
    if (state === 'connected') {
      clearTimeout(this.timers.restart);
      clearTimeout(this.timers.fail);
      this.timers.fail = null;
      this.onConnected?.();
    } else if (state === 'disconnected') {
      // Often recovers on its own; if not, get fresh ICE candidates
      clearTimeout(this.timers.restart);
      this.timers.restart = setTimeout(() => this.requestRestart(), RESTART_AFTER_DISCONNECT_MS);
      this.startFailTimer();
    } else if (state === 'failed') {
      this.requestRestart();
      this.startFailTimer();
    }
  }

  startFailTimer() {
    if (this.timers.fail) return;
    this.timers.fail = setTimeout(() => this.fail('media_failed'), FAIL_AFTER_MS);
  }

  // If the socket is down, the resume after reconnecting restarts ICE anyway
  requestRestart() {
    this.send({ type: 'restart' });
  }

  fail(reason) {
    if (this.closed) return;
    this.close();
    this.onFailed?.(reason);
  }

  end(reason) {
    if (this.closed) return;
    this.close();
    this.onClosed?.(reason);
  }
}

export default SfuSession;
